import { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Printer, ShieldCheck } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { ErroRequisicao, get } from '../../nucleo/api';
import { data as fmtData, nomePessoa } from '../../nucleo/formato';
import { nomesOcultos } from '../../nucleo/discreto';
import { Confirmar } from '../../componentes/sobreposicao';
import { useSessao } from '../../nucleo/sessao';
import { BASE } from './comum';
import { duracaoTexto, type Certificado as CertificadoDados } from './tipos';
import { LinkVoltar } from '../../nucleo/voltar';

const esc = (v: string) => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** Tokens do tema claro (papel branco) lidos do app: a impressão usa as mesmas cores por token, nunca nomes de cor. */
function tokensParaPapel() {
  const raiz = document.documentElement;
  const anterior = raiz.getAttribute('data-tema');
  raiz.setAttribute('data-tema', 'claro');
  const estilo = getComputedStyle(raiz);
  const lista = ['--superficie-1', '--tinta-1', '--tinta-3', '--borda', '--acento'].map((n) => `${n}: ${estilo.getPropertyValue(n).trim()};`).join(' ');
  if (anterior === null) raiz.removeAttribute('data-tema');
  else raiz.setAttribute('data-tema', anterior);
  return lista;
}

/**
 * Página imprimível numa janela própria: só o certificado, em paisagem, sem a moldura do sistema.
 * Cores pelos tokens do tema claro e `lang` do idioma do app, sem imagens nem scripts externos.
 */
function imprimir(c: CertificadoDados, idioma: string, textos: { imprimir: string; titulo: string; certificamos: string; concluiu: string; carga: string; emitido: string; codigo: string; empresa: string; verificado: string; fechar: string }) {
  const w = window.open('', '_blank', 'width=1000,height=720');
  if (!w) return;
  w.document.open();
  w.document.write(`<!doctype html><html lang="${esc(idioma)}"><head><meta charset="utf-8"><title>${esc(textos.titulo)} ${esc(c.codigo)}</title>
<style>
  :root { ${tokensParaPapel()} }
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: var(--tinta-1); background: var(--superficie-1); }
  .moldura { border: 3px double var(--tinta-1); padding: 18mm 16mm; min-height: 170mm; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 6mm; }
  h1 { font-size: 34pt; margin: 0; letter-spacing: 2px; font-weight: normal; text-transform: uppercase; }
  .pequeno { font-size: 13pt; color: var(--tinta-3); margin: 0; }
  .nome { font-size: 30pt; margin: 2mm 0; border-bottom: 1px solid var(--tinta-1); padding: 0 10mm 2mm; }
  .curso { font-size: 22pt; font-style: italic; margin: 0; }
  .rodape { margin-top: 10mm; font-size: 11pt; color: var(--tinta-3); }
  .codigo { font-family: 'Courier New', monospace; font-size: 14pt; color: var(--tinta-1); letter-spacing: 1px; }
  .acoes { text-align: center; padding: 10px; }
  @media print { .acoes { display: none; } }
</style></head><body>
<div class="acoes"><button onclick="window.print()">${esc(textos.imprimir)}</button> <button onclick="window.close()">${esc(textos.fechar)}</button></div>
<div class="moldura">
  <p class="pequeno">${esc(textos.empresa)}</p>
  <h1>${esc(textos.titulo)}</h1>
  <p class="pequeno">${esc(textos.certificamos)}</p>
  <p class="nome">${esc(c.funcionario_nome)}</p>
  <p class="pequeno">${esc(textos.concluiu)}</p>
  <p class="curso">${esc(c.curso_titulo)}</p>
  <p class="pequeno">${esc(textos.carga)}</p>
  <div class="rodape">
    <div>${esc(textos.emitido)}</div>
    <div>${esc(textos.codigo)}: <span class="codigo">${esc(c.codigo)}</span></div>
    <div>${esc(textos.verificado)}</div>
  </div>
</div>
</body></html>`);
  w.document.close();
  w.focus();
}

/** M11.4: certificado interno com nome, curso, carga horária, data e código de verificação (a rota do código confere a autenticidade). */
export function Certificado() {
  const { codigo = '' } = useParams();
  const { t, i18n } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [confirmar, setConfirmar] = useState(false);
  const q = useQuery({ queryKey: ['treinamento', 'certificado', codigo], queryFn: () => get<CertificadoDados>(`/treinamento/certificados/${encodeURIComponent(codigo)}`), retry: false });
  const empresa = s.workspace.tenant_nome;

  const voltar = (
    <LinkVoltar para={`${BASE}/progresso`} className="inline-flex items-center gap-1 text-sm text-tinta-3 hover:text-tinta-1">
      <ArrowLeft className="size-4" aria-hidden />
      {t('certificado.voltar')}
    </LinkVoltar>
  );

  if (q.isPending) return <Carregando />;
  if (q.isError) {
    const naoEncontrado = q.error instanceof ErroRequisicao && q.error.status === 404;
    return (
      <div className="flex flex-col gap-4">
        {voltar}
        {naoEncontrado ? (
          <div role="alert" className="flex min-h-48 flex-col items-center justify-center gap-2 rounded-xl border border-perigo px-4 py-10 text-center">
            <p className="font-medium text-perigo">{t('certificado.invalido')}</p>
            <p className="numero text-sm text-tinta-3">{codigo}</p>
            <p className="max-w-sm text-sm text-tinta-3">{t('certificado.invalidoTexto')}</p>
          </div>
        ) : (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        )}
      </div>
    );
  }
  const c = q.data;
  const carga = duracaoTexto(c.carga_horaria_min);
  const imprimirAgora = () =>
    imprimir(c, i18n.language, {
      imprimir: t('acoes.imprimir'),
      titulo: t('certificado.titulo'),
      certificamos: t('certificado.certificamos'),
      concluiu: t('certificado.concluiu'),
      carga: t('certificado.cargaHoraria', { carga }),
      emitido: t('certificado.emitidoEm', { data: fmtData(c.emitido_em) }),
      codigo: t('certificado.codigo'),
      empresa,
      verificado: t('certificado.verificado'),
      fechar: t('acoes.fechar'),
    });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {voltar}
        <Botao
          variante="primario"
          icone={<Printer className="size-4" />}
          // Modo discreto com a opção de nomes (r9 spec B5): a tela mostra as iniciais; a janela de impressão leva o nome real,
          // então pede confirmação, como a exportação.
          onClick={() => (nomesOcultos() ? setConfirmar(true) : imprimirAgora())}
        >
          {t('acoes.imprimir')}
        </Botao>
        <Confirmar
          aberto={confirmar}
          aoFechar={() => setConfirmar(false)}
          titulo={tn('discreto.exportarTitulo')}
          texto={<p data-aviso-impressao>{tn('discreto.imprimirAviso')}</p>}
          aoConfirmar={() => {
            setConfirmar(false);
            imprimirAgora();
          }}
        />
      </div>

      <article aria-label={t('certificado.titulo')} className="mx-auto flex w-full max-w-3xl min-w-0 flex-col items-center gap-3 rounded-xl border-2 border-acento-forte bg-sup-1 px-6 py-10 text-center sm:px-12">
        <p className="kicker">{empresa}</p>
        <h2 className="text-2xl font-semibold tracking-tight uppercase sm:text-3xl">{t('certificado.titulo')}</h2>
        <p className="text-sm text-tinta-3">{t('certificado.certificamos')}</p>
        <p className="max-w-full border-b border-borda px-4 pb-2 text-2xl font-semibold break-words sm:text-3xl">{nomePessoa(c.funcionario_nome)}</p>
        <p className="text-sm text-tinta-3">{t('certificado.concluiu')}</p>
        <p className="max-w-full text-xl font-medium break-words italic">{c.curso_titulo}</p>
        <p className="text-sm text-tinta-2">{t('certificado.cargaHoraria', { carga })}</p>
        <div className="mt-4 flex flex-col items-center gap-1 text-sm text-tinta-3">
          <p>{t('certificado.emitidoEm', { data: fmtData(c.emitido_em) })}</p>
          <p>
            {t('certificado.codigo')}: <span className="numero text-tinta-1">{c.codigo}</span>
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-verde">
            <ShieldCheck className="size-4" aria-hidden />
            {t('certificado.verificado')}
          </p>
        </div>
      </article>
    </div>
  );
}
